import { useParams, useNavigate } from 'react-router-dom';
import { Link, useLang } from '../i18n';
import { useEffect } from 'react';
import { allArticles } from '../data/blogData';
import SEO from '../components/SEO';
import { SITE_URL } from '../data/siteData';

const WA_URL = 'https://wa.me/34650492859?text=Hola%20Caroline%2C%20me%20gustar%C3%ADa%20saber%20m%C3%A1s%20sobre%20los%20cursos%20de%20Speak%20English%20Now';

function renderBody(markdown) {
  const lines = markdown.trim().split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(<h2 key={key++} className="bp-h2">{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={key++} className="bp-h3">{line.slice(4)}</h3>);
    } else if (line.startsWith('---')) {
      elements.push(<hr key={key++} className="bp-hr" />);
    } else if (line.startsWith('| ')) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      i--;
      const rows = tableLines.filter(r => !r.match(/^\|[-| ]+\|$/));
      elements.push(
        <div key={key++} className="bp-table-wrap">
          <table className="bp-table">
            <tbody>
              {rows.map((row, ri) => {
                const cells = row.split('|').filter(c => c.trim() !== '');
                const Tag = ri === 0 ? 'th' : 'td';
                return (
                  <tr key={ri}>
                    {cells.map((cell, ci) => <Tag key={ci}>{cell.trim()}</Tag>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else if (line.startsWith('- **')) {
      const match = line.match(/^- \*\*(.+?)\*\*(.*)$/);
      if (match) {
        elements.push(
          <li key={key++} className="bp-li bp-li-bold">
            <strong>{match[1]}</strong>{match[2]}
          </li>
        );
      } else {
        elements.push(<li key={key++} className="bp-li">{parseLine(line.slice(2))}</li>);
      }
    } else if (line.startsWith('- ')) {
      elements.push(<li key={key++} className="bp-li">{parseLine(line.slice(2))}</li>);
    } else if (line.match(/^\d+\. /)) {
      const content = line.replace(/^\d+\. /, '');
      elements.push(<li key={key++} className="bp-li bp-li-num">{parseLine(content)}</li>);
    } else if (line.startsWith('**')) {
      elements.push(<p key={key++} className="bp-p bp-bold-label">{parseLine(line)}</p>);
    } else if (line.startsWith('*"') || line.startsWith('*')) {
      const clean = line.replace(/^\*"?/, '').replace(/"?\*$/, '');
      elements.push(<p key={key++} className="bp-p bp-italic"><em>"{clean}"</em></p>);
    } else if (line.trim() === '') {
      elements.push(<div key={key++} className="bp-spacer" />);
    } else if (line.trim()) {
      elements.push(<p key={key++} className="bp-p">{parseLine(line)}</p>);
    }
  }

  return elements;
}

function parseLine(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { path } = useLang();
  const article = allArticles.find(a => a.slug === slug);

  useEffect(() => {
    if (!article) navigate(path('/blog'), { replace: true });
  }, [article, navigate, path]);

  useEffect(() => {
    if (article) {
      document.title = `${article.title} – Speak English Now`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.content = article.excerpt;
    }
    return () => {
      document.title = 'Speak English Now | Clases de Inglés Online';
    };
  }, [article]);

  if (!article) return null;

  const related = allArticles.filter(a => a.slug !== slug && a.cat === article.cat).slice(0, 3);
  const canonical = `${SITE_URL}/blog/${article.slug}`;
  const socialImage = article.img.startsWith('http') ? article.img : `${SITE_URL}${article.img}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.excerpt,
        image: socialImage,
        datePublished: article.dateISO,
        dateModified: article.modifiedISO || article.dateISO,
        inLanguage: 'es',
        mainEntityOfPage: canonical,
        author: { '@type': 'Person', name: article.author },
        publisher: {
          '@type': 'EducationalOrganization',
          name: 'Speak English Now',
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/media/brand-horizontal.png` },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
          { '@type': 'ListItem', position: 3, name: article.title, item: canonical },
        ],
      },
    ],
  };

  return (
    <>
      <SEO title={article.title} description={article.excerpt} keywords={article.keywords} canonical={canonical} ogImage={socialImage} ogType="article" imageAlt={article.title} schema={articleSchema} publishedTime={article.dateISO} modifiedTime={article.modifiedISO || article.dateISO} />
      {/* HERO */}
      <section className="bp-hero">
        <div className="container">
          <div className="bp-breadcrumb">
            <Link to="/">Inicio</Link>
            <span>›</span>
            <Link to="/blog">Blog</Link>
            <span>›</span>
            <span>{article.cat}</span>
          </div>
          <div className="bp-hero-inner">
            <span className="blog-cat-badge">{article.cat}</span>
            <h1 className="bp-title">{article.title}</h1>
            <p className="bp-excerpt">{article.excerpt}</p>
            <div className="bp-meta">
              <img src={article.avatar} alt={article.author} className="bp-avatar" loading="lazy" decoding="async" />
              <div className="bp-meta-text">
                <span className="bp-author">{article.author}</span>
                {article.authorRole && <span className="bp-author-role">{article.authorRole}</span>}
              </div>
              <span className="blog-dot">·</span>
              <span className="bp-date">{article.date}</span>
              <span className="blog-dot">·</span>
              <span className="bp-read">⏱ {article.readTime} lectura</span>
            </div>
          </div>
        </div>
      </section>

      {/* COVER IMAGE */}
      <div className="bp-cover-wrap">
        <div className="container">
          <img src={article.img} alt={article.title} className="bp-cover-img" loading="lazy" decoding="async" />
        </div>
      </div>

      {/* ARTICLE BODY + SIDEBAR */}
      <section className="section-pad">
        <div className="container">
          <div className="bp-layout">
            {/* BODY */}
            <article className="bp-body">
              <div className="bp-content">
                {renderBody(article.body)}
              </div>

              {/* SHARE */}
              <div className="bp-share">
                <span className="bp-share-label">¿Te ha gustado? Comparte:</span>
                <div className="bp-share-btns">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(article.title + ' – https://speakenglishnow.es/blog/' + article.slug)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="bp-share-wa"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent('https://speakenglishnow.es/blog/' + article.slug)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="bp-share-tw"
                  >
                    Twitter / X
                  </a>
                </div>
              </div>

              {/* BACK */}
              <div className="bp-back">
                <Link to="/blog" className="bp-back-link">← Volver al Blog</Link>
              </div>
            </article>

            {/* SIDEBAR */}
            <aside className="bp-sidebar">
              {/* CTA */}
              <div className="bp-cta-card">
                <div className="bp-cta-icon">🎓</div>
                <h3 className="bp-cta-title">¿Quieres practicar esto con Caroline?</h3>
                <p className="bp-cta-desc">Clases online en grupos reducidos y preparación FCE y CAE.</p>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="btn-wa-cta w-full" style={{ justifyContent: 'center' }}>
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Hablar con Caroline
                </a>
                <Link to="/curso-online" className="btn-primary mt-12 w-full" style={{ justifyContent: 'center' }}>Ver Curso Pro</Link>
              </div>

              {/* RELATED */}
              {related.length > 0 && (
                <div className="bp-related">
                  <h4 className="bp-related-title">Artículos relacionados</h4>
                  <div className="bp-related-list">
                    {related.map(r => (
                      <Link key={r.slug} to={`/blog/${r.slug}`} className="bp-related-item">
                        <img src={r.cardImg || r.img} alt={r.title} className="bp-related-img" loading="lazy" decoding="async" />
                        <div className="bp-related-text">
                          <span className="bp-related-cat">{r.cat}</span>
                          <span className="bp-related-name">{r.title}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* MORE ARTICLES */}
      {allArticles.filter(a => a.slug !== slug && a.cat !== article.cat).length > 0 && (
        <section className="section-pad bg-light">
          <div className="container text-center">
            <span className="section-label">SIGUE LEYENDO</span>
            <h2 className="section-title">Más artículos del blog</h2>
            <div className="cards-3 mt-40">
              {allArticles.filter(a => a.slug !== slug).slice(0, 3).map(a => (
                <Link key={a.slug} to={`/blog/${a.slug}`} className="blog-card" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="blog-card-img">
                    <img src={a.cardImg || a.img} alt={a.title} loading="lazy" decoding="async" />
                    <span className="blog-cat-badge">{a.cat}</span>
                  </div>
                  <div className="blog-card-body">
                    <h3 className="blog-card-title">{a.title}</h3>
                    <p className="blog-card-excerpt">{a.excerpt}</p>
                    <div className="blog-meta">
                      <span className="blog-author">{a.author}</span>
                      <span className="blog-dot">·</span>
                      <span className="blog-read">{a.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
